import { z } from 'zod'
import { moneySchema, optionalText, pageQuerySchema, versionSchema } from './common.js'
import type { EmployeeRef, NationalityClass, PayComponentKind } from './hr.js'

/**
 * Payroll (`payroll` module, ADR-048): monthly runs over the hr employee file, priced by one shared calculator
 * (`payroll-calc.ts`). Social Protection Fund contributions are tenant data (`contribution rules`): a rule per
 * insurance branch, for Omani or non-Omani employees, with employee and employer rates, a base, a ceiling and
 * the dates it is in force, so a change in the law is a new row, never a code change.
 */

const dateSchema = z.iso.date()
const optionalDate = dateSchema.nullish().transform((v) => v ?? null)

/** A percentage with up to 4 decimals, 0–100 ("7.5", "11", "0.5"). */
export const payrollPercentSchema = z
  .string()
  .trim()
  .regex(/^\d{1,3}(\.\d{1,4})?$/, 'Invalid percentage')
  .refine((v) => Number(v) <= 100, 'At most 100')

/** A multiplier with up to 2 decimals, 1–5 ("1.25"). */
const multiplierSchema = z
  .string()
  .trim()
  .regex(/^\d(\.\d{1,2})?$/, 'Invalid rate')
  .refine((v) => Number(v) >= 1 && Number(v) <= 5, 'Between 1 and 5')

/** Days or hours with up to 2 decimals. */
export const payrollQuantitySchema = z
  .string()
  .trim()
  .regex(/^\d{1,4}(\.\d{1,2})?$/, 'Invalid number')

// ── settings ──────────────────────────────────────────────────────────────────

/** FIXED_30: a day's pay is a month's pay / 30 (Omani practice); CALENDAR: / the days of the month. */
export const DAY_RATE_BASES = ['FIXED_30', 'CALENDAR'] as const
export type DayRateBasis = (typeof DAY_RATE_BASES)[number]

export const WAGE_BASES = ['BASIC', 'GROSS'] as const
export type WageBasis = (typeof WAGE_BASES)[number]

export const GRATUITY_SCOPES = ['NON_OMANI', 'ALL'] as const
export type GratuityScope = (typeof GRATUITY_SCOPES)[number]

export const sickBandSchema = z.object({
  /** The last sick day of the year this band covers (the bands follow each other from day 1). */
  toDay: z.number().int().min(1).max(366),
  payPercent: payrollPercentSchema,
})
export type SickBand = z.output<typeof sickBandSchema>

export const gratuityTierSchema = z.object({
  /** Completed years of service the tier starts at (the first tier starts at 0). */
  fromYear: z.number().int().min(0).max(60),
  /** Days of the gratuity wage owed for each year of service in this tier (30 = one month). */
  daysPerYear: z.number().min(0).max(90),
})
export type GratuityTier = z.output<typeof gratuityTierSchema>

const payrollSettingsFields = z.object({
  /** Commercial registration printed in the WPS file; empty: the company profile's CR number. */
  employerCr: optionalText(30),
  /** The company's bank as the WPS file names it (BIC / short code). */
  payerBankCode: optionalText(20),
  /** The account salaries are paid from. */
  payerAccount: optionalText(34),
  dayRateBasis: z.enum(DAY_RATE_BASES),
  overtimeBase: z.enum(WAGE_BASES),
  /** An hour's pay is the overtime wage / this number of hours (240 = 30 days × 8 hours). */
  overtimeHoursDivisor: z
    .string()
    .trim()
    .regex(/^\d{1,3}(\.\d{1,2})?$/, 'Invalid hours')
    .refine((v) => Number(v) >= 1, 'At least 1'),
  /** Day overtime: × 1.25 under the Labour Law. */
  overtimeDayRate: multiplierSchema,
  /** Night, rest-day and public-holiday overtime: × 1.50. */
  overtimeNightRate: multiplierSchema,
  /** Overtime counts in the contribution wage. */
  overtimeContributory: z.boolean(),
  /** Sick-day pay over a calendar year, in bands; days after the last band are unpaid. */
  sickBands: z.array(sickBandSchema).max(10),
  /** The wage a leave day is encashed at, / 30. */
  encashmentBase: z.enum(WAGE_BASES),
  gratuityEnabled: z.boolean(),
  gratuityScope: z.enum(GRATUITY_SCOPES),
  gratuityTiers: z.array(gratuityTierSchema).min(1).max(5),
  /** Part years count pro rata (otherwise completed years only). */
  gratuityProRata: z.boolean(),
  /** Below this service nothing is paid at exit (the provision still accrues). */
  gratuityMinServiceYears: z.number().min(0).max(10),
  /** Service after this date earns no gratuity (the expatriate savings scheme replaces it). */
  gratuityAccrualUntil: optionalDate,
  /** Omani minimum wage, the total and its basic (warnings below them); 0 turns the check off. */
  minimumWageOmani: moneySchema,
  minimumBasicOmani: moneySchema,
  /** Voluntary deductions above this share of the gross raise a warning (statutory ones excluded). */
  deductionCapPercent: payrollPercentSchema,
  /** Maker-checker: whoever calculated a run cannot approve it. */
  requireSeparateApprover: z.boolean(),
})

const settingsRules = (v: z.output<typeof payrollSettingsFields>, ctx: z.RefinementCtx) => {
  v.sickBands.forEach((b, i) => {
    const prev = v.sickBands[i - 1]
    if (prev && b.toDay <= prev.toDay)
      ctx.addIssue({
        code: 'custom',
        path: ['sickBands', i, 'toDay'],
        message: 'Must follow the band before',
      })
  })
  v.gratuityTiers.forEach((t, i) => {
    const prev = v.gratuityTiers[i - 1]
    if (i === 0 && t.fromYear !== 0)
      ctx.addIssue({
        code: 'custom',
        path: ['gratuityTiers', 0, 'fromYear'],
        message: 'The first tier starts at 0',
      })
    if (prev && t.fromYear <= prev.fromYear)
      ctx.addIssue({
        code: 'custom',
        path: ['gratuityTiers', i, 'fromYear'],
        message: 'Must follow the tier before',
      })
  })
}

export const payrollSettingsSchema = payrollSettingsFields.superRefine(settingsRules)
export type PayrollSettingsValue = z.output<typeof payrollSettingsSchema>

export const payrollSettingsUpdateSchema = payrollSettingsFields
  .extend({ version: versionSchema })
  .superRefine(settingsRules)
export type PayrollSettingsUpdate = z.input<typeof payrollSettingsUpdateSchema>

/**
 * Labour Law starting values (RD 53/2023 as read from secondary sources; ASM-257). Sick bands: days 1–21 full
 * pay, 22–35 at 75%, 36–70 at 50%, 71–182 at 35%. Gratuity: one month's basic wage per year of service for
 * non-Omanis, frozen when the expatriate savings scheme starts (19 Jul 2027, ASM-259).
 */
export const DEFAULT_PAYROLL_SETTINGS: PayrollSettingsValue = {
  employerCr: null,
  payerBankCode: null,
  payerAccount: null,
  dayRateBasis: 'FIXED_30',
  overtimeBase: 'BASIC',
  overtimeHoursDivisor: '240',
  overtimeDayRate: '1.25',
  overtimeNightRate: '1.5',
  overtimeContributory: false,
  sickBands: [
    { toDay: 21, payPercent: '100' },
    { toDay: 35, payPercent: '75' },
    { toDay: 70, payPercent: '50' },
    { toDay: 182, payPercent: '35' },
  ],
  encashmentBase: 'GROSS',
  gratuityEnabled: true,
  gratuityScope: 'NON_OMANI',
  gratuityTiers: [{ fromYear: 0, daysPerYear: 30 }],
  gratuityProRata: true,
  gratuityMinServiceYears: 1,
  gratuityAccrualUntil: '2027-07-18',
  minimumWageOmani: '325.000',
  minimumBasicOmani: '225.000',
  deductionCapPercent: '25',
  requireSeparateApprover: true,
}

export interface PayrollSettings extends PayrollSettingsValue {
  /** The CR number the WPS file uses (employerCr or the company profile's). */
  effectiveEmployerCr: string | null
  version: number
  updatedAt: string | null
  updatedBy: { id: string; name: string } | null
}

// ── social contribution rules ─────────────────────────────────────────────────

/** Who a rule applies to, by the employee's nationality. */
export const CONTRIBUTION_SCOPES = ['OMANI', 'NON_OMANI', 'ALL'] as const
export type ContributionScope = (typeof CONTRIBUTION_SCOPES)[number]

/**
 * The wage a rule's percentages apply to: the contribution wage (components marked contributory), the basic
 * salary or every earning; all after unpaid days.
 */
export const CONTRIBUTION_BASES = ['CONTRIBUTORY', 'BASIC', 'GROSS'] as const
export type ContributionBase = (typeof CONTRIBUTION_BASES)[number]

export function contributionApplies(scope: ContributionScope, employee: NationalityClass): boolean {
  return scope === 'ALL' || scope === employee
}

const ruleCode = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z][A-Z0-9_]{1,39}$/, 'Use 2–40 letters, digits or underscores, starting with a letter')

export const contributionRuleInputSchema = z
  .object({
    code: ruleCode,
    name: z.string().trim().min(1).max(150),
    nameAr: optionalText(150),
    scope: z.enum(CONTRIBUTION_SCOPES),
    /** Deducted from the employee's pay. */
    employeeRate: payrollPercentSchema,
    /** Paid by the employer on top of the pay. */
    employerRate: payrollPercentSchema,
    base: z.enum(CONTRIBUTION_BASES).default('CONTRIBUTORY'),
    /** The most of the base a month the rates apply to (OMR 3,000 for the SPF); empty: no ceiling. */
    ceiling: moneySchema
      .refine((v) => Number(v) > 0, 'Must be more than zero')
      .nullish()
      .transform((v) => v ?? null),
    effectiveFrom: dateSchema,
    effectiveTo: optionalDate,
    /** Where the rate comes from (decree, circular, link). */
    source: optionalText(500),
    active: z.boolean().default(true),
  })
  .superRefine((v, ctx) => {
    if (Number(v.employeeRate) === 0 && Number(v.employerRate) === 0)
      ctx.addIssue({ code: 'custom', path: ['employerRate'], message: 'Give the employee or employer rate' })
    if (v.effectiveTo && v.effectiveTo < v.effectiveFrom)
      ctx.addIssue({ code: 'custom', path: ['effectiveTo'], message: 'Ends before it starts' })
  })
export type ContributionRuleInput = z.input<typeof contributionRuleInputSchema>
export type ContributionRuleValue = z.output<typeof contributionRuleInputSchema>

export const contributionRuleUpdateSchema = z.object({
  rule: contributionRuleInputSchema,
  version: versionSchema,
})
export type ContributionRuleUpdate = z.input<typeof contributionRuleUpdateSchema>

/** ACTIVE today, SCHEDULED (starts later), ENDED (ended before today) or OFF (switched off). */
export type ContributionRuleStatus = 'ACTIVE' | 'SCHEDULED' | 'ENDED' | 'OFF'

export function contributionRuleStatus(
  rule: { active: boolean; effectiveFrom: string; effectiveTo: string | null },
  today: string,
): ContributionRuleStatus {
  if (!rule.active) return 'OFF'
  if (rule.effectiveFrom > today) return 'SCHEDULED'
  if (rule.effectiveTo && rule.effectiveTo < today) return 'ENDED'
  return 'ACTIVE'
}

export interface ContributionRule extends ContributionRuleValue {
  id: string
  status: ContributionRuleStatus
  /** Priced on an approved run: its rates and dates can no longer change (end it and add a new row). */
  inUse: boolean
  version: number
}

const rule = (
  code: string,
  name: string,
  nameAr: string,
  scope: ContributionScope,
  employeeRate: string,
  employerRate: string,
  effectiveFrom: string,
  extra: Partial<ContributionRuleValue> = {},
): ContributionRuleValue => ({
  code,
  name,
  nameAr,
  scope,
  employeeRate,
  employerRate,
  base: 'CONTRIBUTORY',
  ceiling: '3000.000',
  effectiveFrom,
  effectiveTo: null,
  source: 'Social Protection Law (RD 52/2023) and RD 60/2025; verify with the Social Protection Fund',
  active: true,
  ...extra,
})

/**
 * The Social Protection Fund branches as researched for the HRM plan (hrm/03 §13.1, ASM-171 there, ASM-260
 * here: UNVERIFIED, every row editable). Omani private-sector employees: employee 8% (old age 7.5%, job
 * security 0.5%), employer 14.5% (old age 11%, work injury 1%, job security 0.5%, maternity 1%, sick and other
 * leave 1% from 19 Jul 2026), on a contribution wage capped at OMR 3,000. Non-Omani employees: work injury
 * 0.5% + 0.5% from 19 Jul 2028 and the employer's 9% end-of-service savings on basic from 19 Jul 2027.
 */
export const OMAN_CONTRIBUTION_PRESET: readonly ContributionRuleValue[] = [
  rule(
    'SPF_OLD_AGE',
    'Old-age, disability and death insurance',
    'تأمين الشيخوخة والعجز والوفاة',
    'OMANI',
    '7.5',
    '11',
    '2024-07-19',
  ),
  rule(
    'SPF_WORK_INJURY',
    'Work injuries and occupational diseases insurance',
    'تأمين إصابات العمل والأمراض المهنية',
    'OMANI',
    '0',
    '1',
    '2024-07-19',
  ),
  rule(
    'SPF_JOB_SECURITY',
    'Job security insurance',
    'تأمين الأمان الوظيفي',
    'OMANI',
    '0.5',
    '0.5',
    '2024-07-19',
  ),
  rule('SPF_MATERNITY', 'Maternity leave insurance', 'تأمين إجازات الأمومة', 'OMANI', '0', '1', '2024-07-19'),
  rule(
    'SPF_SICK_LEAVE',
    'Sick and other leave insurance',
    'تأمين الإجازات المرضية والإجازات الأخرى',
    'OMANI',
    '0',
    '1',
    '2026-07-19',
  ),
  rule(
    'SPF_WORK_INJURY_EXPAT',
    'Work injuries insurance (non-Omani)',
    'تأمين إصابات العمل (غير العمانيين)',
    'NON_OMANI',
    '0.5',
    '0.5',
    '2028-07-19',
  ),
  rule(
    'SPF_EXPAT_SAVINGS',
    'End-of-service savings (non-Omani)',
    'نظام الادخار لنهاية الخدمة (غير العمانيين)',
    'NON_OMANI',
    '0',
    '9',
    '2027-07-19',
    { base: 'BASIC', ceiling: null },
  ),
]

// ── runs ──────────────────────────────────────────────────────────────────────

export const PAYROLL_RUN_STATUSES = [
  'DRAFT',
  'CALCULATED',
  'APPROVED',
  'PAID',
  'CANCELLED',
  'REVERSED',
] as const
export type PayrollRunStatus = (typeof PAYROLL_RUN_STATUSES)[number]

/** A run in one of these keeps its period: a second run of the month is refused. */
export const OPEN_PAYROLL_RUN_STATUSES: readonly PayrollRunStatus[] = [
  'DRAFT',
  'CALCULATED',
  'APPROVED',
  'PAID',
]

export const payrollRunCreateSchema = z.object({
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
  /** The day salaries reach the banks (WPS: within the first days of the next month). */
  payDate: dateSchema,
  note: optionalText(500),
})
export type PayrollRunCreateInput = z.input<typeof payrollRunCreateSchema>

export const payrollRunUpdateSchema = z.object({
  payDate: dateSchema,
  note: optionalText(500),
  version: versionSchema,
})
export type PayrollRunUpdateInput = z.input<typeof payrollRunUpdateSchema>

export const payrollRunVersionSchema = z.object({ version: versionSchema })
export type PayrollRunVersionInput = z.input<typeof payrollRunVersionSchema>

export const payrollRunPaySchema = z.object({
  /** The date the bank paid the salaries. */
  paidOn: dateSchema,
  /** The cash or bank account they were paid from. */
  treasuryAccountId: z.uuid(),
  reference: optionalText(100),
  version: versionSchema,
})
export type PayrollRunPayInput = z.input<typeof payrollRunPaySchema>

export const payrollRunReasonSchema = z.object({
  reason: z.string().trim().min(1).max(500),
  version: versionSchema,
})
export type PayrollRunReasonInput = z.input<typeof payrollRunReasonSchema>

export const PAYROLL_RUN_SORTS = ['period', 'number', 'status', 'netTotal'] as const

export const payrollRunListQuerySchema = pageQuerySchema.extend({
  status: z.enum(PAYROLL_RUN_STATUSES).optional(),
  year: z.coerce.number().int().min(2000).max(2100).optional(),
})
export type PayrollRunListQuery = z.input<typeof payrollRunListQuerySchema>

export const PAYROLL_ALERT_SEVERITIES = ['ERROR', 'WARNING', 'INFO'] as const
export type PayrollAlertSeverity = (typeof PAYROLL_ALERT_SEVERITIES)[number]

/**
 * What a calculation found. ERROR blocks approval (no salary, negative net); WARNING and INFO are shown with
 * the payslip and on the run.
 */
export const PAYROLL_ALERT_CODES = [
  'NO_SALARY',
  'NO_BASIC',
  'NET_NEGATIVE',
  'MINIMUM_WAGE',
  'DEDUCTION_CAP',
  'CEILING_APPLIED',
  'MISSING_BANK_DETAILS',
  'MISSING_CIVIL_ID',
  'SUSPENDED',
  'NO_CONTRIBUTION_RULES',
  'ENDS_IN_PERIOD',
  'JOINS_IN_PERIOD',
] as const
export type PayrollAlertCode = (typeof PAYROLL_ALERT_CODES)[number]

export interface PayrollAlert {
  code: PayrollAlertCode
  severity: PayrollAlertSeverity
  /** Values for the message (amounts, dates, rule codes). */
  params: Record<string, string>
}

export interface PayrollRunAlert extends PayrollAlert {
  /** Null for a run-level alert. */
  employee: EmployeeRef | null
}

export interface PayrollRunListItem {
  id: string
  number: string
  year: number
  month: number
  periodStart: string
  periodEnd: string
  payDate: string
  status: PayrollRunStatus
  employees: number
  grossTotal: string
  deductionsTotal: string
  netTotal: string
  /** Gross + the employer's contributions + the end-of-service provision. */
  employerCost: string
  createdAt: string
}

export interface PayrollContributionTotal {
  code: string
  name: string
  nameAr: string | null
  employees: number
  base: string
  employee: string
  employer: string
}

export interface PayrollRun extends PayrollRunListItem {
  note: string | null
  employeeContributions: string
  employerContributions: string
  /** The end-of-service provision of the month (non-Omanis still employed). */
  gratuityProvision: string
  /** Gratuity paid to those who left in the month. */
  gratuityPaid: string
  alerts: PayrollRunAlert[]
  errors: number
  warnings: number
  contributions: PayrollContributionTotal[]
  calculatedAt: string | null
  calculatedBy: { id: string; name: string } | null
  approvedAt: string | null
  approvedBy: { id: string; name: string } | null
  paidAt: string | null
  paidBy: { id: string; name: string } | null
  paidOn: string | null
  paymentReference: string | null
  treasuryAccount: { id: string; name: string } | null
  journalEntry: { id: string; number: string } | null
  paymentEntry: { id: string; number: string } | null
  cancelledAt: string | null
  reversedAt: string | null
  reason: string | null
  /** True when an hr change (hire, end, salary, input) came after the calculation. */
  stale: boolean
  version: number
}

// ── inputs ────────────────────────────────────────────────────────────────────

export const payrollAdjustmentInputSchema = z.object({
  componentId: z.uuid(),
  amount: moneySchema.refine((v) => Number(v) > 0, 'Must be more than zero'),
  note: optionalText(200),
})

/** One employee's variable inputs for a run: absences, sick days, overtime and one-off lines. */
export const payrollInputSchema = z.object({
  unpaidDays: payrollQuantitySchema.default('0'),
  sickDays: payrollQuantitySchema.default('0'),
  overtimeHours: payrollQuantitySchema.default('0'),
  overtimeNightHours: payrollQuantitySchema.default('0'),
  /** Annual-leave days paid out (at exit, or by agreement). */
  encashmentDays: payrollQuantitySchema.default('0'),
  note: optionalText(500),
  adjustments: z.array(payrollAdjustmentInputSchema).max(20).default([]),
})
export type PayrollInputValue = z.input<typeof payrollInputSchema>

export interface PayrollInput {
  employeeId: string
  unpaidDays: string
  sickDays: string
  overtimeHours: string
  overtimeNightHours: string
  encashmentDays: string
  note: string | null
  adjustments: {
    componentId: string
    code: string
    name: string
    nameAr: string | null
    kind: PayComponentKind
    amount: string
    note: string | null
  }[]
}

// ── payslips ──────────────────────────────────────────────────────────────────

export const PAYSLIP_LINE_KINDS = ['EARNING', 'DEDUCTION', 'EMPLOYER'] as const
export type PayslipLineKind = (typeof PAYSLIP_LINE_KINDS)[number]

/** Where a line comes from; computed lines (overtime, unpaid days, contributions, gratuity) carry no component. */
export const PAYSLIP_LINE_SOURCES = [
  'SALARY',
  'ADJUSTMENT',
  'OVERTIME',
  'OVERTIME_NIGHT',
  'UNPAID_LEAVE',
  'SICK_LEAVE',
  'ENCASHMENT',
  'CONTRIBUTION',
  'GRATUITY_PROVISION',
  'GRATUITY',
  /** ADR-049: an approved commission statement paid on the payslip (its account is commissions payable). */
  'COMMISSION',
] as const
export type PayslipLineSource = (typeof PAYSLIP_LINE_SOURCES)[number]

export interface PayslipLine {
  kind: PayslipLineKind
  source: PayslipLineSource
  code: string
  name: string
  nameAr: string | null
  componentId: string | null
  ruleId: string | null
  /** Days or hours the line counts, when it counts any. */
  quantity: string | null
  /** The rate: a percentage for contributions, a multiplier for overtime, the day or hour rate otherwise. */
  rate: string | null
  /** The amount the rate applied to (the contribution base, the hourly wage…). */
  base: string | null
  amount: string
  note: string | null
  /** The commission statement a COMMISSION line pays (ADR-049). Optional for older fixtures. */
  commissionStatementId?: string | null
}

export interface PayslipListItem {
  id: string
  employee: EmployeeRef & {
    nationalityClass: NationalityClass
    jobTitle: string
    jobTitleAr: string | null
    orgUnitName: string | null
    orgUnitNameAr: string | null
  }
  paidDays: string
  basic: string
  gross: string
  deductions: string
  employeeContributions: string
  employerContributions: string
  net: string
  alerts: PayrollAlert[]
  hasInputs: boolean
}

export interface Payslip extends PayslipListItem {
  run: {
    id: string
    number: string
    year: number
    month: number
    periodStart: string
    periodEnd: string
    payDate: string
    status: PayrollRunStatus
  }
  employedFrom: string
  employedTo: string
  daysInPeriod: number
  unpaidDays: string
  sickDays: string
  overtimeHours: string
  overtimeNightHours: string
  hireDate: string
  endedOn: string | null
  /** Masked to its last 4 characters without employees.manage. */
  civilNumber: string | null
  spfNumber: string | null
  payMethod: string
  bankCode: string | null
  /** Masked to its last 4 characters without employees.view_pay. */
  iban: string | null
  contributoryWage: string
  gratuityLiability: string
  lines: PayslipLine[]
  input: PayrollInput | null
}

/** The Social Protection Fund schedule of a run: one row per employee, a column pair per rule. */
export interface ContributionScheduleRow {
  employee: EmployeeRef & {
    nationalityClass: NationalityClass
    spfNumber: string | null
    civilNumber: string | null
  }
  contributoryWage: string
  lines: { code: string; base: string; employee: string; employer: string }[]
  employeeTotal: string
  employerTotal: string
}

export interface ContributionSchedule {
  run: { id: string; number: string; year: number; month: number; status: PayrollRunStatus }
  rules: { code: string; name: string; nameAr: string | null; employeeRate: string; employerRate: string }[]
  rows: ContributionScheduleRow[]
  employeeTotal: string
  employerTotal: string
}

/** Payroll alerts in words (EN/AR) are the web's job; this is the shortest English reading, for files and logs. */
export const PAYROLL_ALERT_SEVERITY: Record<PayrollAlertCode, PayrollAlertSeverity> = {
  NO_SALARY: 'ERROR',
  NO_BASIC: 'WARNING',
  NET_NEGATIVE: 'ERROR',
  MINIMUM_WAGE: 'WARNING',
  DEDUCTION_CAP: 'WARNING',
  CEILING_APPLIED: 'INFO',
  MISSING_BANK_DETAILS: 'WARNING',
  MISSING_CIVIL_ID: 'WARNING',
  SUSPENDED: 'INFO',
  NO_CONTRIBUTION_RULES: 'WARNING',
  ENDS_IN_PERIOD: 'INFO',
  JOINS_IN_PERIOD: 'INFO',
}

/** The calendar month of a run as ISO dates. */
export function payrollPeriod(year: number, month: number): { start: string; end: string } {
  const start = `${year}-${String(month).padStart(2, '0')}-01`
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate()
  return { start, end: `${year}-${String(month).padStart(2, '0')}-${String(last).padStart(2, '0')}` }
}

/** An employee's payslips, newest first (the employee page's Payslips tab). */
export interface EmployeePayslipItem {
  id: string
  run: { id: string; number: string; year: number; month: number; status: PayrollRunStatus; payDate: string }
  paidDays: string
  gross: string
  deductions: string
  net: string
}

/** The refusals payroll returns (problem+json `code`), which the web puts in words. */
export const PAYROLL_ERROR_CODES = [
  /** 409: the month already has a run (cancel or reverse it first). */
  'PAYROLL_PERIOD_TAKEN',
  /** 422: the command does not apply in the run's status. */
  'PAYROLL_RUN_STATUS',
  /** 422: approve a calculated run. */
  'PAYROLL_NOT_CALCULATED',
  /** 422: employees, salaries, inputs, rules or settings changed since the calculation: calculate again. */
  'PAYROLL_STALE',
  /** 422: a payslip has an error alert (no salary, negative net). */
  'PAYROLL_HAS_ERRORS',
  /** 422: whoever calculated the run cannot approve it (maker-checker setting). */
  'PAYROLL_SAME_PERSON',
  /** 422: salaries are paid from an active cash or bank account. */
  'TREASURY_ACCOUNT_INVALID',
  /** 422: the WPS file needs the employer's CR number and the payer bank and account (Payroll settings). */
  'WPS_NOT_SET_UP',
  /** 422: the employee is not employed in the run's month. */
  'PAYROLL_EMPLOYEE_NOT_IN_PERIOD',
  /**
   * 422: months are approved in order: an earlier month's run is still open, or a later month is already
   * approved (approving or reversing out of order would mis-state the gratuity provision and sick-day bands).
   */
  'PAYROLL_OUT_OF_ORDER',
  /** 422: a rule priced on an approved run keeps its code, scope, rates, base, ceiling and start date. */
  'CONTRIBUTION_RULE_IN_USE',
  /** 409: another rule has this code from the same date. */
  'CONTRIBUTION_RULE_DUPLICATE',
] as const
export type PayrollErrorCode = (typeof PAYROLL_ERROR_CODES)[number]
